import { FC, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ROUTES from "@/data/Routes";
import { Check, Info } from "lucide-react";
import { ActionLink } from "@/components/UI/Buttons";
import InfoTooltip from "@/components/UI/InfoTooltip";
import CompareProgramsModal, {
  type CompareProgramsModalTabKey,
} from "@/components/Memberships/CompareProgramsModal";
import membershipLight from "@/assets/images/memberships/membership-light.png";
import membershipDark from "@/assets/images/memberships/membership-dark.png";
import doctor1 from "@/assets/images/memberships/doctor-1.png";
import doctor2 from "@/assets/images/memberships/doctor-2.png";
import compareIconLight from "@/assets/images/memberships/material-symbols-light_compare-arrows-rounded.svg";
import compareIconDark from "@/assets/images/memberships/material-symbols-dark_compare-arrows-rounded.svg";
import { Button } from "@/components/UI/Buttons/Button";
import { useCart } from "@/components/UI/Buttons/CartContext";
import { toast } from "sonner";
import { MEMBERSHIPS } from "@/data/membershipProducts";
import { pushEvent } from "@/utils/gtm";

type MembershipAddToCartButtonProps = {
  productId?: number;
  label: string;
  imageUrl: string;
  variant: "ink" | "white";
};

const MembershipAddToCartButton: FC<MembershipAddToCartButtonProps> = ({
  productId,
  label,
  imageUrl,
  variant,
}) => {
  const { addToCart } = useCart();
  const [isLoading, setIsLoading] = useState(false);

  const onClick = async () => {
    if (!productId) {
      toast.error("Trūksta produkto ID");
      return;
    }

    const membership = Object.values(MEMBERSHIPS).find(
      (m) => m.id === productId,
    );

    setIsLoading(true);
    try {
      await addToCart({ id: productId, quantity: 1, image_url: imageUrl });

      if (membership) {
        pushEvent("add_to_cart", {
          value: membership.firstYearPrice,
          currency: "EUR",
          item: membership.title,
        });
      }

      toast.success("Pridėta į krepšelį");
    } catch (error) {
      console.error("Error adding membership to cart:", error);
      toast.error("Nepavyko pridėti į krepšelį");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size="sm"
      onClick={onClick}
      disabled={isLoading}
      onHover
      hoverGradientLight
      className="h-[63px] w-full text-[16px] font-light"
    >
      {isLoading ? "Kraunama..." : label}
    </Button>
  );
};

const coreFeatures = [
  "25 sveikatos sritis ir jų įvertinimą",
  "Platų kraujo ištyrimą",
  "Gyvensenos genetinį ištyrimą",
  "VO₂max testavimą",
  "Kūno sudėties ir jėgos matavimą",
  "6 medicinos ekspertų konsultacijas",
  "12 savaičių gyvensenos korekcijos programą",
  "Bazinę maisto papildų prenumeratą",
  "Pakartotinius kraujo tyrimus",
];

const apexFeatures = [
  "Reguliarūs susitikimai su gyvensenos specialistu",
  "10+ sveikatos sričių įvertinimas",
  "Viso klinikinio genomo vertinimas",
  "Vaistų toleravimo farmakogenetika",
  "Gyd. genetiko retų ligų identifikavimas",
  "Gyd. genetiko šeimos planavimo strategija",
  "Nuolatinis gliukozės stebėjimas (CGS)",
  "Asmeninė paciento vadyba ir priežiūra",
  "Pakartotiniai VO₂max, kūno sudėties ir jėgos matavimai",
];

const coreIdealFor = [
  "Pilno sveikatos įvertinimo",
  "Subalansuotos energijos ir miego",
  "Kasdienio streso ir savijautos kontrolės",
  "Aiškaus, gydytojų pagrįsto ir prižiūrimo veiksmų plano",
];

const apexIdealFor = [
  "Visko CORE narystėje",
  "Paveldimų ligų rizikų valdymo",
  "Pakartotinių tyrimų ir pokyčio stebėjimo",
  "Nuolatinės priežiūros ir sprendimų koregavimo",
];

interface MembershipCardProps {
  color: "light" | "dark";
  title: string;
  image: string;
  description: string;
  price: string;
  featuresTitle: string;
  features: string[];
  isHome?: boolean;
  onCompare: () => void;
}

const MembershipCard: FC<MembershipCardProps> = ({
  color,
  title,
  image,
  description,
  price,
  featuresTitle,
  features,
  isHome,
  onCompare,
}) => {
  const isDark = color === "dark";
  const tooltipId = useId();
  const tooltipRootRef = useRef<HTMLSpanElement | null>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  useEffect(() => {
    if (!isTooltipOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      const root = tooltipRootRef.current;
      if (!root) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      setIsTooltipOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsTooltipOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isTooltipOpen]);

  const cartProductId =
    title === "CORE" ? MEMBERSHIPS.CORE.id : MEMBERSHIPS.APEX.id;
  const addToCartVariant = title === "CORE" ? "ink" : "white";
  const imageUrl = title === "CORE" ? membershipLight : membershipDark;

  return (
    <div
      className={`flex w-auto flex-1 flex-col items-center gap-[30px] self-stretch rounded-[25px] p-6 lg:w-[540px] ${
        isDark ? "bg-dark" : "bg-[#EBEBEB]"
      }`}
    >
      <img
        src={image}
        alt={isDark ? "APEX narystė" : "CORE narystė"}
        className={`h-auto max-w-[280px] object-contain`}
      />

      <div className="flex flex-1 flex-col items-start gap-7.5 self-stretch">
        <p
          className={`w-full text-center text-[18px] leading-[150%] font-light ${isDark ? "text-white" : "text-dark"}`}
        >
          {description}
        </p>

        <div className="flex items-center justify-between self-stretch">
          <span
            className={`flex items-center gap-1.5 text-[18px] leading-[150%] font-light ${isDark ? "text-white" : "text-dark"}`}
          >
            Pirmų metų narystė
            <span
              ref={tooltipRootRef}
              className="group relative inline-flex items-center justify-center"
            >
              <button
                type="button"
                aria-label="Narystės kainos informacija"
                aria-expanded={isTooltipOpen}
                aria-controls={tooltipId}
                onClick={() => setIsTooltipOpen((open) => !open)}
                className="cursor-help p-1 opacity-50 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
              >
                <Info size={14} />
              </button>
              <span
                id={tooltipId}
                role="tooltip"
                className={`pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 ${
                  isTooltipOpen ? "visible opacity-100" : ""
                }`}
              >
                <span
                  className={`block w-[200px] rounded-md px-3 py-2 text-center text-[13px] leading-tight font-normal whitespace-normal shadow-xl ${
                    isDark ? "text-dark bg-white" : "bg-dark text-white"
                  }`}
                >
                  Į pirmų metų narystę įtrauktas išsamus pradinis ištyrimas,
                  įskaitant genetikos analizę, todėl nuo antrų metų narystės
                  kaina – iki €500
                </span>
              </span>
            </span>
          </span>
          <span
            className={`text-[18px] leading-[150%] font-light ${isDark ? "text-white" : "text-dark"}`}
          >
            {price}
          </span>
        </div>

        <div className="flex w-full flex-col gap-3">
          {isHome && (
            <ActionLink
              to={ROUTES.MEMBERSHIPS}
              variant={isDark ? "light" : "dark"}
              showIcon={false}
              hoverGradientLight
              onHover
              className="h-[63px] self-stretch px-[26px] py-[20px] text-[16px] font-light transition-opacity hover:opacity-90 md:px-[26px] md:py-[20px]"
            >
              Sužinoti daugiau
            </ActionLink>
          )}

          {!isHome && (
            <MembershipAddToCartButton
              productId={cartProductId}
              label={`Pasirinkti ${title}`}
              imageUrl={imageUrl}
              variant={addToCartVariant}
            />
          )}
        </div>

        <div className="flex flex-1 flex-col items-start justify-between self-stretch">
          <div className="flex flex-col gap-7.5">
            <span
              className={`text-sm font-semibold ${isDark ? "text-white" : "text-dark"}`}
            >
              {featuresTitle}
            </span>
            <div className="flex flex-col gap-3">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Check
                    size={16}
                    className={`mt-0.5 shrink-0 ${isDark ? "text-white" : "text-dark"}`}
                  />
                  <span
                    className={`text-sm leading-[150%] ${isDark ? "text-gray-300" : "text-body"}`}
                  >
                    {feature}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-4 self-stretch">
            <div className={"h-px w-full border-b border-[#404040]"} />
            <button
              onClick={onCompare}
              className={`flex items-center gap-2 text-sm font-light ${isDark ? "text-white hover:text-gray-300" : "text-dark hover:text-gray-600"}`}
            >
              <img
                src={isDark ? compareIconDark : compareIconLight}
                alt="Compare"
              />
              Palyginti programas
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface MembershipIntroCardProps {
  color: "light" | "dark";
  title: string;
  image: string;
  description: string;
  idealForTitle: string;
  idealFor: string[];
  isHome?: boolean;
}

const MembershipIntroCard: FC<MembershipIntroCardProps> = ({
  color,
  title,
  image,
  description,
  idealForTitle,
  idealFor,
}) => {
  const isDark = color === "dark";

  return (
    <div
      className={`flex flex-1 flex-col items-start gap-[30px] rounded-[26px] px-[25px] py-10 pb-[60px] md:w-[600px] ${
        isDark ? "bg-[#181818]" : "bg-[#e7e7e7]"
      }`}
    >
      <div className="flex w-full flex-col items-start gap-2.5">
        <h3
          className={`text-[30px] leading-[1.35] font-medium ${isDark ? "text-white" : "text-dark"}`}
        >
          {title}
        </h3>
        <p
          className={`text-[18px] leading-[1.4] font-normal ${isDark ? "text-white" : "text-gray-900"}`}
        >
          {description}
        </p>
      </div>

      <div className="flex h-[260px] w-full justify-center">
        <img src={image} alt={title} className="h-full object-contain" />
      </div>

      <div className="flex w-full flex-col items-start gap-5">
        <p
          className={`text-[18px] leading-[1.35] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
        >
          {idealForTitle}
        </p>
        <div className="flex w-full flex-col items-start gap-3">
          {idealFor.map((item, index) => (
            <div key={index} className="flex w-full items-center gap-3">
              <Check
                size={16}
                className={`shrink-0 ${isDark ? "text-white" : "text-dark"}`}
              />
              <p
                className={`flex-1 text-[16px] leading-[1.5] font-normal ${isDark ? "text-white" : "text-gray-900"}`}
              >
                {item}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export interface MembershipsProps {
  showCTA?: boolean;
  cardVariant?: "home" | "page";
  isHome?: boolean;
  sectionTitle?: string;
  sectionDescription?: string;
  isCompareOpen?: boolean;
  onCompareOpenChange?: (open: boolean) => void;
  compareOpenToTab?: CompareProgramsModalTabKey;
  onCompareOpenToTabChange?: (tab: CompareProgramsModalTabKey) => void;
  renderCompareModal?: boolean;
}

const DEFAULT_HOME_SECTION_TITLE = "Narystės pritaikytos Jūsų tikslams";
const DEFAULT_HOME_SECTION_DESCRIPTION =
  "Siūlome du narystės lygius, kurie skiriasi diagnostikos, pakartotinių tyrimų ir klinikinės komandos apimtimi";

const Memberships: FC<MembershipsProps> = ({
  showCTA = true,
  cardVariant = "home",
  isHome = false,
  sectionTitle = DEFAULT_HOME_SECTION_TITLE,
  sectionDescription = DEFAULT_HOME_SECTION_DESCRIPTION,
  isCompareOpen: isCompareOpenProp,
  onCompareOpenChange,
  compareOpenToTab: compareOpenToTabProp,
  onCompareOpenToTabChange,
  renderCompareModal = true,
}) => {
  const [isCompareOpenInternal, setIsCompareOpenInternal] = useState(false);
  const [compareOpenToTabInternal, setCompareOpenToTabInternal] =
    useState<CompareProgramsModalTabKey>("vertinimai");

  const isCompareOpen =
    isCompareOpenProp !== undefined ? isCompareOpenProp : isCompareOpenInternal;
  const setCompareOpen = onCompareOpenChange || setIsCompareOpenInternal;
  const setCompareOpenToTab =
    onCompareOpenToTabChange || setCompareOpenToTabInternal;
  const compareOpenToTab = compareOpenToTabProp ?? compareOpenToTabInternal;

  return (
    <section
      className={`flex flex-col items-center bg-white px-4 ${isHome ? "pb-15 lg:pb-25" : "pt-15 lg:pt-25"}`}
    >
      <div className="flex w-full max-w-[1220px] flex-col items-start gap-10">
        {cardVariant === "home" && (
          <div className="flex flex-1 flex-col items-center justify-center gap-5 self-stretch">
            <h2 className="text-6 text-dark md:text-8.5 text-center leading-[120%] font-light">
              {sectionTitle}
            </h2>

            <p className="text-body max-w-[600px] text-center text-base leading-[150%]">
              {sectionDescription}
            </p>
          </div>
        )}

        <div
          className={`gap-5 self-stretch ${cardVariant === "page" ? "flex flex-col lg:flex-row" : "flex flex-wrap"}`}
        >
          {cardVariant === "home" ? (
            <>
              <MembershipCard
                color="light"
                title="CORE"
                image={membershipLight}
                description="Jūsų visapusiškas pagrindinis sveikatos įvertinimas"
                price="€2500"
                featuresTitle="CORE apima:"
                features={coreFeatures}
                isHome={isHome}
                onCompare={() => {
                  setCompareOpenToTab("vertinimai");
                  setCompareOpen(true);
                }}
              />
              <MembershipCard
                color="dark"
                title="APEX"
                image={membershipDark}
                description="Pažangiausia diagnostika su visus metus trunkančia priežiūra"
                price="€3900"
                featuresTitle="Viskas CORE - plius:"
                features={apexFeatures}
                isHome={isHome}
                onCompare={() => {
                  setCompareOpenToTab("vertinimai");
                  setCompareOpen(true);
                }}
              />
            </>
          ) : (
            <>
              <MembershipIntroCard
                color="light"
                title="CORE"
                image={membershipLight}
                description="Jūsų pagrindinis, visapusiškos sveikatos, įvertinimas"
                idealForTitle="Idealiai tinka tiems, kurie nori:"
                idealFor={coreIdealFor}
                isHome={isHome}
              />
              <MembershipIntroCard
                color="dark"
                title="APEX"
                image={membershipDark}
                description="Pažangiausia diagnostika su visus metus trunkančia priežiūra"
                idealForTitle="Idealiai tinka tiems, kurie nori:"
                idealFor={apexIdealFor}
                isHome={isHome}
              />
            </>
          )}
        </div>

        <div className="flex w-full flex-col items-center justify-center gap-2.5 self-stretch md:flex-row md:gap-4">
          <div className="flex w-full max-w-[569px] flex-wrap items-center justify-center gap-[10px] md:justify-between">
            <InfoTooltip
              content={
                <>
                  Kraujo, genetiniai, VO₂max ir kiti tyrimai gali būti
                  pasirenkami atskirai. Dėl asmeninio pasiūlymo –{" "}
                  <Link to={ROUTES.CONSULTATION} className="underline">
                    registruokitės konsultacijai
                  </Link>
                  .
                </>
              }
              variant="dark"
            >
              <div className="flex items-center gap-3 rounded-full bg-[#e7e7e7] py-2 pr-3 pl-2">
                <Check
                  size={20}
                  className="bg-dark rounded-full p-1 text-white"
                />
                <span className="text-dark text-base">
                  Galimybė įsigyti pavienes paslaugas
                </span>
              </div>
            </InfoTooltip>
            <InfoTooltip
              content={
                <>
                  Mokėjimą galime padalinti į 3 lygias dalis arba pasiūlyti
                  išsimokėjimą be pabrangimo per „Inbank". Dėl asmeninio
                  pasiūlymo –{" "}
                  <Link to={ROUTES.CONSULTATION} className="underline">
                    registruokitės konsultacijai
                  </Link>
                  .
                </>
              }
              variant="dark"
            >
              <div className="flex items-center gap-3 rounded-full bg-[#e7e7e7] py-2 pr-3 pl-2">
                <Check
                  size={20}
                  className="bg-dark rounded-full p-1 text-white"
                />
                <span className="text-dark text-base">
                  Galimas mokėjimas dalimis
                </span>
              </div>
            </InfoTooltip>
          </div>
        </div>

        {cardVariant === "page" && (
          <div className="flex w-full flex-col items-center justify-center gap-5 self-stretch pb-12.5 md:pb-20">
            <div className="flex w-full max-w-[569px] flex-row items-center justify-center gap-3 md:flex-col md:gap-5">
              <ActionLink
                variant="dark"
                showIcon={false}
                to={ROUTES.CONSULTATION}
                className="min-w-0 flex-1 basis-0 text-[14px] md:w-full md:flex-none"
                hoverGradientLight
                onHover
              >
                Sužinoti daugiau
              </ActionLink>
              <ActionLink
                variant="light"
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  setCompareOpen(true);
                }}
                className="min-w-0 flex-1 basis-0 border border-gray-400 text-[14px] md:w-full md:flex-none"
                showIcon={false}
                hoverGradientLight
                onHover
              >
                Palyginti narystes
              </ActionLink>
            </div>
          </div>
        )}

        {showCTA && (
          <div className="mx-auto flex max-w-[910px] flex-col gap-5 self-stretch rounded-[24px] bg-[#F5F5F5] p-6 md:flex-row md:items-center md:justify-between md:px-8 md:py-5">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-5">
              <div className="flex items-center gap-4">
                <div className="flex shrink-0 -space-x-3">
                  <img
                    src={doctor1}
                    alt=""
                    className="h-12 w-12 shrink-0 rounded-full border-2 border-[#F5F5F5] object-cover md:h-[52px] md:w-[52px]"
                  />
                  <img
                    src={doctor2}
                    alt=""
                    className="h-12 w-12 shrink-0 rounded-full border-2 border-[#F5F5F5] object-cover md:h-[52px] md:w-[52px]"
                  />
                </div>
                <span className="text-dark text-lg leading-[1.2] font-medium md:hidden">
                  Nežinote, kuri narystė Jums tinkamiausia?
                </span>
              </div>

              <div className="flex flex-col gap-2 md:gap-1">
                <span className="text-dark hidden text-xl leading-[1.2] font-medium md:block">
                  Nežinote, kuri narystė Jums tinkamiausia?
                </span>
                <span className="text-body text-[15px] leading-[1.5] md:max-w-[480px] md:text-base">
                  <Link
                    to={ROUTES.CONSULTATION}
                    className="hover:text-dark underline transition-colors"
                  >
                    Parašykite žinutę
                  </Link>{" "}
                  arba registruokitės konsultacijai ir kartu rasime geriausią
                  sprendimą.
                </span>
              </div>
            </div>

            <ActionLink
              to={ROUTES.CONSULTATION}
              variant="dark"
              className="w-full md:w-auto"
              hoverGradientLight
              onHover
            >
              Registruotis konsultacijai
            </ActionLink>
          </div>
        )}
      </div>

      {renderCompareModal && (
        <CompareProgramsModal
          isOpen={isCompareOpen}
          onOpenChange={setCompareOpen}
          openToTab={compareOpenToTab}
        />
      )}
    </section>
  );
};

export default Memberships;
